import {useMsal} from '@azure/msal-react';
import React, {useCallback, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';

import {useLocalesMgr, useSetUI, useUI} from 'coreHooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Button from 'brandComponents/Button';
import Popup from 'brandComponents/Popup';
import Typography from 'brandComponents/Typography';
import {useBasket} from 'brandHooks/useBasket';

import messages from './messages';

import styles from './CountryConfirmRedirectPopup.module.scss';

const CountryConfirmRedirectPopup = () => {
	const {managerRepository} = useGeneralProvider();
	const {instance: msal} = useMsal();
	const localesMgr = useLocalesMgr();
	const basket = useBasket();
	const setUI = useSetUI();
	const {countryCode, languageCode} = useUI('countryConfirmRedirectObj');
	const intl = useIntl();
	const [isConfirmed, setIsConfirmed] = useState<boolean>(false);

	const translatedMessages = {
		heading: intl.formatMessage(messages.heading),
		description: intl.formatMessage(messages.description),
		cancel: intl.formatMessage(messages.cancel),
		continue: intl.formatMessage(messages.continue),
	};

	const handleRedirectFunc = useCallback(async () => {
		setIsConfirmed(true);

		try {
			await Promise.all([
				msal.logoutPopup(),
				localesMgr.updateLocale(countryCode, languageCode)
			]);
		} catch (err) {
			console.error(err);
		} finally {
			setIsConfirmed(false);
			managerRepository.getPopupsMgr().closeAllPopups();
			managerRepository.getFlyoutsMgr().closeFlyout('countrySelector');
			setUI.resetCountryConfirmRedirectObj();
		}
	}, [languageCode, countryCode, basket]);

	const handleOnCancel = useCallback(() => {
		managerRepository.getPopupsMgr().closePopup('countryConfirmRedirect');
	}, []);

	useEffect(() => {
		return () => {
			setIsConfirmed(false);
		};
	}, []);

	if (!countryCode || !languageCode) {
		return null;
	}

	return (
		<Popup
			id="countryConfirmRedirect"
			onClose={!isConfirmed && handleOnCancel}
			showCloseButton={!isConfirmed}
			className={styles['country-redirect-popup']}
			popupContentClassName={styles['country-redirect-popup__content']}
		>
			<Typography variant="h4" classNames={styles['country-redirect-popup__title']}>
				{translatedMessages.heading}
			</Typography>

			<div className={styles['country-redirect-popup__description']}>
				{translatedMessages.description}
			</div>

			<div className={styles['country-redirect-popup__cta-wrapper']}>
				<Button onClick={handleOnCancel} disabled={isConfirmed}>
					{translatedMessages.cancel}
				</Button>
				<Button loading={isConfirmed} onClick={handleRedirectFunc} variant="primary-black">
					{translatedMessages.continue}
				</Button>
			</div>
		</Popup>
	);
};

export default CountryConfirmRedirectPopup;
